import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";

const FeaturesAdmin = () => {
  const [features, setFeatures] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editingFeature, setEditingFeature] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  
  // Initialize form state
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    iconType: "image", // "image" or "class"
    iconValue: "", // either URL or class name
    category: "AI Features",
    order: 0,
  });

  // Fetch features data
  useEffect(() => {
    const fetchFeaturesData = async () => {
      try {
        setIsLoading(true);
        const response = await fetch('https://imagetohtmlaiproject25-d0525ebf3976.herokuapp.com/api/features-section');
        
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        
        const result = await response.json();
        
        if (result.success) {
          setFeatures(result.data);
        } else {
          setError(result.message || 'Failed to fetch features data');
        }
      } catch (err) {
        console.error('Error fetching features data:', err);
        setError('Network error or server not responding');
      } finally {
        setIsLoading(false);
      }
    };
    fetchFeaturesData();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    
    if (type === 'radio') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        // Reset icon value when switching types
        iconValue: name === 'iconType' ? '' : prev.iconValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: name === 'order' ? parseInt(value) || 0 : value
      }));
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      iconType: "image",
      iconValue: "",
      category: "AI Features",
      order: features.length > 0 ? Math.max(...features.map(f => f.order)) + 1 : 0,
    });
    setEditingFeature(null);
    setShowAddForm(false);
  };

  // Handle form submission (add or update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    
    try {
      const url = editingFeature 
        ? `https://imagetohtmlaiproject25-d0525ebf3976.herokuapp.com/api/features-section/${editingFeature._id}`
        : 'https://imagetohtmlaiproject25-d0525ebf3976.herokuapp.com/api/features-section';
      
      const method = editingFeature ? 'PUT' : 'POST';
      
      // Prepare payload with icon field
      const payload = {
        title: formData.title,
        description: formData.description,
        icon: formData.iconValue || (formData.iconType === "image" ? "" : "fi fi-tr-memo-circle-check"),
        category: formData.category,
        order: formData.order,
      };
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success(`Feature ${editingFeature ? 'updated' : 'added'} successfully!`);
        
        // Update features list
        if (editingFeature) {
          setFeatures(prev => 
            prev.map(f => f._id === editingFeature._id ? result.data : f)
          );
        } else {
          setFeatures(prev => [...prev, result.data]);
        }
        
        resetForm();
      } else {
        toast.error(result.message || `Failed to ${editingFeature ? 'update' : 'add'} feature`);
      }
    } catch (err) {
      console.error(`Error ${editingFeature ? 'updating' : 'adding'} feature:`, err);
      toast.error('Network error or server not responding');
    } finally {
      setIsSaving(false);
    }
  };

  // Edit a feature
  const handleEdit = (feature) => {
    // Determine if the icon is a URL or a class
    const isUrl = feature.icon && feature.icon.startsWith('http');
    
    setFormData({
      title: feature.title,
      description: feature.description,
      iconType: isUrl ? "image" : "class",
      iconValue: feature.icon || "",
      category: feature.category,
      order: feature.order,
    });
    setEditingFeature(feature);
    setShowAddForm(true);
  };

  // Delete a feature
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this feature?')) {
      return;
    }
    
    try {
      const response = await fetch(`https://imagetohtmlaiproject25-d0525ebf3976.herokuapp.com/api/features-section/${id}`, {
        method: 'DELETE',
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success('Feature deleted successfully!');
        setFeatures(prev => prev.filter(f => f._id !== id));
      } else {
        toast.error(result.message || 'Failed to delete feature');
      }
    } catch (err) {
      console.error('Error deleting feature:', err);
      toast.error('Network error or server not responding');
    }
  };

  // Toggle feature active status
  const handleToggleActive = async (id, isActive) => {
    try {
      const response = await fetch(`https://imagetohtmlaiproject25-d0525ebf3976.herokuapp.com/api/features-section/${id}/toggle`, {
        method: 'PATCH',
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success(`Feature ${result.data.isActive ? 'activated' : 'deactivated'} successfully!`);
        setFeatures(prev => 
          prev.map(f => f._id === id ? { ...f, isActive: result.data.isActive } : f)
        );
      } else {
        toast.error(result.message || 'Failed to toggle feature status');
      }
    } catch (err) {
      console.error('Error toggling feature status:', err);
      toast.error('Network error or server not responding');
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading features data...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        <h4>Error Loading Features Data</h4>
        <p>{error}</p>
        <button 
          className="btn btn-outline-danger"
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="features-admin">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Features Section Management</h2>
        <button 
          className="btn btn-primary"
          onClick={() => {
            resetForm();
            setShowAddForm(true);
          }}
        >
          Add New Feature
        </button>
      </div>

      {showAddForm && (
        <div className="card mb-4">
          <div className="card-header">
            <h5>{editingFeature ? 'Edit Feature' : 'Add New Feature'}</h5>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor="title" className="form-label">Title</label>
                  <input
                    type="text"
                    className="form-control"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                {/* <div className="col-md-6 mb-3">
                  <label htmlFor="category" className="form-label">Category</label>
                  <input
                    type="text"
                    className="form-control"
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                  />
                </div> */}
                
                <div className="col-12 mb-3">
                  <label className="form-label">Icon Type</label>
                  <div>
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="iconType"
                        id="iconTypeImage"
                        value="image"
                        checked={formData.iconType === 'image'}
                        onChange={handleInputChange}
                      />
                      <label className="form-check-label" htmlFor="iconTypeImage">Image URL</label>
                    </div>
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="iconType"
                        id="iconTypeClass"
                        value="class"
                        checked={formData.iconType === 'class'}
                        onChange={handleInputChange}
                      />
                      <label className="form-check-label" htmlFor="iconTypeClass">Icon Class</label>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6 mb-3">
                  <label htmlFor="iconValue" className="form-label">
                    {formData.iconType === 'image' ? 'Image URL' : 'Icon Class'}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="iconValue"
                    name="iconValue"
                    value={formData.iconValue}
                    onChange={handleInputChange}
                    placeholder={
                      formData.iconType === 'image' 
                        ? "https://example.com/image.jpg" 
                        : "fi fi-tr-hourglass-start"
                    }
                  />
                  <div className="form-text">
                    {formData.iconType === 'image' 
                      ? "Enter a URL for the feature image (JPG, PNG, GIF, WebP)" 
                      : "Enter an icon class (e.g., 'fi fi-tr-hourglass-start')"}
                  </div>
                </div>
                
                {/* <div className="col-md-6 mb-3">
                  <label htmlFor="order" className="form-label">Order</label>
                  <input
                    type="number"
                    className="form-control"
                    id="order"
                    name="order"
                    value={formData.order}
                    onChange={handleInputChange}
                    min="0"
                  />
                  <div className="form-text">Lower numbers appear first</div>
                </div> */}
                
                <div className="col-12 mb-3">
                  <label htmlFor="description" className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    id="description"
                    name="description"
                    rows="3"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                  ></textarea>
                </div>
                
                {/* Image Preview - only show for image type */}
                {formData.iconType === 'image' && formData.iconValue && (
                  <div className="col-12 mb-3">
                    <label className="form-label">Image Preview</label>
                    <div className="image-preview-container">
                      <img 
                        src={formData.iconValue} 
                        alt="Feature preview" 
                        className="img-thumbnail"
                        style={{ maxWidth: '200px' }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://placehold.co/200x200?text=Invalid+Image+URL';
                        }}
                      />
                    </div>
                  </div>
                )}
                
                {/* Icon Preview - only show for class type */}
                {formData.iconType === 'class' && formData.iconValue && (
                  <div className="col-12 mb-3">
                    <label className="form-label">Icon Preview</label>
                    <div className="icon-preview-container">
                      <i className={formData.iconValue} style={{ fontSize: '2rem' }}></i>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="d-flex justify-content-end">
                <button 
                  type="button" 
                  className="btn btn-secondary me-2"
                  onClick={resetForm}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : (editingFeature ? 'Update Feature' : 'Add Feature')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h5>Current Features</h5>
        </div>
        <div className="card-body">
          {features.length === 0 ? (
            <p className="text-center">No features found. Add your first feature!</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    {/* <th>Order</th> */}
                    <th>Icon</th>
                    <th>Title</th>
                    {/* <th>Category</th> */}
                    <th>Description</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {features
                    .sort((a, b) => a.order - b.order)
                    .map(feature => (
                    <tr key={feature._id}>
                      {/* <td>{feature.order}</td> */}
                      <td>
                        {feature.icon ? (
                          feature.icon.startsWith('http') ? (
                            <img 
                              src={feature.icon} 
                              alt={feature.title} 
                              className="feature-thumbnail"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'https://placehold.co/50x50?text=No+Image';
                              }}
                            />
                          ) : (
                            <div className="feature-icon-thumbnail">
                              <i className={feature.icon}></i>
                            </div>
                          )
                        ) : (
                          <div className="feature-placeholder-thumbnail">
                            <i className="fi fi-tr-image-slash" />
                          </div>
                        )}
                      </td>
                      <td>{feature.title}</td>
                      {/* <td>{feature.category}</td> */}
                      <td>{feature.description}</td>
                      <td>
                        <span className={`badge ${feature.isActive ? 'bg-success' : 'bg-danger'}`}>
                          {feature.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <button 
                          className="btn btn-sm btn-outline-primary me-1"
                          onClick={() => handleEdit(feature)}
                        >
                          Edit
                        </button>
                        <button 
                          className={`btn btn-sm ${feature.isActive ? 'btn-outline-warning' : 'btn-outline-success'} me-1`}
                          onClick={() => handleToggleActive(feature._id, feature.isActive)}
                        >
                          {feature.isActive ? 'Disable' : 'Enable'}
                        </button>
                        <button 
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(feature._id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      
      <style jsx>{`
        .feature-thumbnail {
          width: 50px;
          height: 50px;
          object-fit: cover;
          border-radius: 4px;
        }
        
        .feature-icon-thumbnail {
          width: 50px;
          height: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          color: #555;
        }
        
        .feature-placeholder-thumbnail {
          width: 50px;
          height: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #f0f0f0;
          border-radius: 4px;
          color: #888;
        }
        
        .icon-preview-container {
          padding: 10px;
          background-color: #f8f9fa;
          border-radius: 4px;
          display: inline-block;
        }
      `}</style>
    </div>
  );
};

export default FeaturesAdmin;